import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSelector } from "react-redux";
import AppPaginatedTable from "components/ui/AppPaginatedTable";

const PAID_STATUS = "PAID";

const getLinkStatusBadgeStyle = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === PAID_STATUS) {
    return {
      backgroundColor: "#E6F7E6",
      color: "#22C55E",
    };
  }
  return {
    backgroundColor: "#FFF9E6",
    color: "#FF8C00",
  };
};

const formatLinkStatusLabel = (status, t) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === PAID_STATUS) return t("Paid");
  return t("Pending");
};

const PaymentLinkTable = ({ paymentLinks = { data: [] }, searchTerm = "" }) => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredLinks = useMemo(() => {
    const raw = paymentLinks?.data || [];
    const term = searchTerm?.trim().toLowerCase();
    if (!term) return raw;

    return raw.filter((row) => {
      const store = String(row?.storeName ?? "").toLowerCase();
      const cart = String(row?.cartId ?? "").toLowerCase();
      const creator = String(row?.createdBy ?? "").toLowerCase();
      const amount = String(row?.amount ?? "").toLowerCase();
      const status = String(row?.linkStatus ?? "").toLowerCase();
      return (
        store.includes(term) ||
        cart.includes(term) ||
        creator.includes(term) ||
        amount.includes(term) ||
        status.includes(term)
      );
    });
  }, [paymentLinks?.data, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const columns = [
    {
      title: t("Store name"),
      dataIndex: "storeName",
      align: "initial",
      key: "storeName",
    },
    {
      title: t("Payment link id"),
      dataIndex: "cartId",
      align: "initial",
      key: "cartId",
    },
    {
      key: "Amount",
      title: t("Amount"),
      dataIndex: "amount",
      render: (val) =>
        new Intl.NumberFormat(currentLanguage, {
          style: "currency",
          currency: "AED",
        }).format(val),
    },
    {
      title: t("Created by"),
      dataIndex: "createdBy",
      align: "initial",
      key: "Created By",
    },
    {
      title: t("Created at"),
      dataIndex: "createdAt",
      align: "initial",
      key: "createdAt",
      render: (val, row) => {
        const ts = val ?? row?.createdAt;
        if (!ts) return "";
        return moment(ts).format("DD/MM/YYYY hh:mm A");
      },
    },
    {
      title: t("Status"),
      dataIndex: "linkStatus",
      align: "initial",
      key: "linkStatus",
      render: (val) => (
        <span
          className="inline-flex min-w-[98px] items-center justify-center rounded-full px-4 py-2 text-sm font-medium"
          style={getLinkStatusBadgeStyle(val)}
        >
          {formatLinkStatusLabel(val, t)}
        </span>
      ),
    },
  ];

  return (
    <div className="w-full">
      <AppPaginatedTable
        columns={columns}
        data={filteredLinks}
        clientSidePagination={true}
        page={page}
        pageSize={pageSize}
        total={filteredLinks.length}
        rowIndexKey="_id"
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage);
          setPageSize(nextPageSize);
        }}
      />
    </div>
  );
};

export default PaymentLinkTable;
